import { signal } from '@preact/signals';
import type { Sample, StatusResponse } from './types.ts';

const TOKEN_KEY = 'accreto.admin.token';
const POLL_MS = 10_000;
/** ~1h of history at the 10s poll cadence; session-only, nothing persisted. */
const HISTORY_CAP = 360;

export const token = signal<string>(localStorage.getItem(TOKEN_KEY) ?? '');
export const data = signal<StatusResponse | null>(null);
export const error = signal<string | null>(null);
export const fetching = signal(false);
/** Per-app ring buffer of numeric metrics since the tab opened. */
export const history = signal<Record<string, Sample[]>>({});

export function setToken(value: string): void {
  localStorage.setItem(TOKEN_KEY, value);
  token.value = value;
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  token.value = '';
  data.value = null;
  history.value = {};
}

function appendSamples(status: StatusResponse): void {
  const t = Date.now();
  const next: Record<string, Sample[]> = { ...history.value };
  for (const app of status.apps) {
    const m = app.metrics;
    if (!m) continue;
    const sample: Sample = {
      t,
      rooms: m.rooms ?? 0,
      connections: m.connections ?? 0,
      tickMsMax: m.simTickMsMax ?? 0,
      gapMsMax: m.simGapMsMax ?? 0,
      gamesStarted: m.gamesStartedTotal ?? 0,
      gamesFinished: m.gamesFinishedTotal ?? 0,
    };
    const buf = [...(next[app.name] ?? []), sample];
    next[app.name] = buf.length > HISTORY_CAP ? buf.slice(-HISTORY_CAP) : buf;
  }
  history.value = next;
}

export async function refresh(): Promise<void> {
  if (!token.value || fetching.value) return;
  fetching.value = true;
  try {
    const res = await fetch('/api/status', {
      headers: { 'x-admin-token': token.value },
    });
    if (res.status === 401) {
      clearToken();
      error.value = null;
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const status = (await res.json()) as StatusResponse;
    data.value = status;
    error.value = null;
    appendSamples(status);
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    fetching.value = false;
  }
}

/** Poll while the tab is visible; refresh immediately when it becomes visible. */
export function startPolling(): void {
  setInterval(() => {
    if (document.visibilityState === 'visible') void refresh();
  }, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
  void refresh();
}
